import { useCallback, useEffect, useRef } from 'react';
import { InterventionTemplateControllerApi } from '../api/generated';
import type {
  CreateInterventionTemplateRequest,
  InterventionTemplateResponse,
  UpdateInterventionTemplateRequest
} from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  removeTemplate,
  selectInterventionTemplates,
  selectInterventionTemplatesError,
  selectInterventionTemplatesLoading,
  setError,
  setLoading,
  setTemplates,
  upsertTemplate
} from '../store/interventionTemplates/interventionTemplatesSlice';
import { getErrorMessage } from '../utils/errorMessageUtils';

export interface UseInterventionTemplatesResult {
  templates: InterventionTemplateResponse[];
  isLoading: boolean;
  loadError: string | null;
  refresh: () => Promise<void>;
  createTemplate: (
    payload: CreateInterventionTemplateRequest
  ) => Promise<InterventionTemplateResponse>;
  updateTemplate: (
    id: string,
    payload: UpdateInterventionTemplateRequest
  ) => Promise<InterventionTemplateResponse>;
  deleteTemplate: (id: string) => Promise<void>;
}

export function useInterventionTemplates(): UseInterventionTemplatesResult {
  const dispatch = useAppDispatch();
  const templates = useAppSelector(selectInterventionTemplates);
  const isLoading = useAppSelector(selectInterventionTemplatesLoading);
  const loadError = useAppSelector(selectInterventionTemplatesError);
  const hasFetchedRef = useRef(false);

  const refresh = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const api = new InterventionTemplateControllerApi(createApiConfiguration());
      const response = await api.listInterventionTemplates();
      dispatch(setTemplates(response.data));
    } catch (e) {
      dispatch(setError(getErrorMessage(e, 'Failed to load interventions')));
      throw e;
    }
  }, [dispatch]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    refresh().catch(() => {
      // error already surfaced via slice state
    });
  }, [refresh]);

  const createTemplate = useCallback(
    async (payload: CreateInterventionTemplateRequest) => {
      const api = new InterventionTemplateControllerApi(createApiConfiguration());
      const response = await api.createInterventionTemplate({
        createInterventionTemplateRequest: payload
      });
      dispatch(upsertTemplate(response.data));
      return response.data;
    },
    [dispatch]
  );

  const updateTemplate = useCallback(
    async (id: string, payload: UpdateInterventionTemplateRequest) => {
      const api = new InterventionTemplateControllerApi(createApiConfiguration());
      const response = await api.updateInterventionTemplate({
        interventionTemplateId: id,
        updateInterventionTemplateRequest: payload
      });
      dispatch(upsertTemplate(response.data));
      return response.data;
    },
    [dispatch]
  );

  const deleteTemplate = useCallback(
    async (id: string) => {
      const api = new InterventionTemplateControllerApi(createApiConfiguration());
      await api.deleteInterventionTemplate({ interventionTemplateId: id });
      dispatch(removeTemplate(id));
    },
    [dispatch]
  );

  return {
    templates,
    isLoading,
    loadError,
    refresh,
    createTemplate,
    updateTemplate,
    deleteTemplate
  };
}
